import { useState, useEffect, useCallback } from "react";
import { useFormBuilder } from "../hooks/useFormBuilder";
import { useIntegrations } from "../hooks/useIntegrations";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { TEMPLATE_LIST, getTemplate } from "../Components/FormTemplates";
import { IntegrationConfigEditor } from "../Components/IntegrationConfigEditor";
import LogicBuilder, { FlowPreview } from "../Components/LogicBuilder";

const FIELD_TYPES = [
    "input",
    "textarea",
    "select",
    "radio",
    "checkbox",
    "email",
    "number",
    "date",
    // Hidden, never rendered to the respondent — its value comes from an expression.
    "calculated",
];

// Choice types whose options can carry per-option point values (scoring).
const SCORABLE_TYPES = ["select", "radio", "checkbox"];

const FONT_OPTIONS = ["Inter", "Poppins", "Roboto", "Open Sans", "JetBrains Mono", "Lato", "Nunito"];

function FormEdit() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const { getForm, updateForm, getSettings, error } = useFormBuilder();

    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [activeTab, setActiveTab] = useState("fields");
    const [previewDevice, setPreviewDevice] = useState("desktop");
    const [logicPanelIdx, setLogicPanelIdx] = useState(null);
    const [showFlowPreview, setShowFlowPreview] = useState(false);
    const [customDomain, setCustomDomain] = useState("");

    const APP_URL =
        process.env.REACT_APP_URL || process.env.REACT_APP_API_URL
            ? process.env.REACT_APP_API_URL?.replace("api.", "platform.")
            : "http://localhost:3000";

    // Verified custom domain takes precedence over the default platform domain.
    const formBaseUrl = customDomain ? `https://${customDomain}` : APP_URL;

    const loadForm = useCallback(async () => {
        setFetching(true);
        const data = await getForm(formId);
        if (data) {
            setForm(data);
        }
        setFetching(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formId]);

    useEffect(() => {
        loadForm();
    }, [loadForm]);

    useEffect(() => {
        (async () => {
            const data = await getSettings();
            if (data?.customDomain?.domain && data.customDomain.verified) {
                setCustomDomain(data.customDomain.domain);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = async () => {
        if (!form) return;
        setSaving(true);
        const result = await updateForm(formId, {
            title: form.title,
            description: form.description,
            fields: form.fields,
            theme: form.theme,
            logo: form.logo,
            captchaEnabled: form.captchaEnabled,
            submitButtonText: form.submitButtonText,
            successMessage: form.successMessage,
            successPlacement: form.successPlacement,
            notifyEmail: form.notifyEmail,
            isPublished: form.isPublished,
            display_mode: form.display_mode,
        });
        if (result) {
            toast("Form saved!");
            setForm(result);
        } else {
            toast("Failed to save");
        }
        setSaving(false);
    };

    const handlePublishToggle = async () => {
        if (!form) return;
        const newVal = !form.isPublished;
        const result = await updateForm(formId, { isPublished: newVal });
        if (result) {
            setForm(result);
            toast(newVal ? "Form published!" : "Form unpublished");
        }
    };

    // Field operations
    const updateField = (index, key, value) => {
        const updated = [...form.fields];
        updated[index] = { ...updated[index], [key]: value };
        setForm({ ...form, fields: updated });
    };

    const addField = () => {
        const newField = {
            fieldId: `f_${Date.now()}`,
            label: "New Field",
            type: "input",
            name: `field_${form.fields.length + 1}`,
            placeholder: "",
            isRequired: false,
            options: [],
            limit: null,
        };
        setForm({ ...form, fields: [...form.fields, newField] });
    };

    const removeField = (index) => {
        const updated = form.fields.filter((_, i) => i !== index);
        setForm({ ...form, fields: updated });
    };

    const moveField = (index, direction) => {
        const updated = [...form.fields];
        const target = index + direction;
        if (target < 0 || target >= updated.length) return;
        [updated[index], updated[target]] = [updated[target], updated[index]];
        setForm({ ...form, fields: updated });
    };

    const updateFieldOptions = (index, optionsStr) => {
        const options = optionsStr.split(",").map((o) => o.trim());
        updateField(index, "options", options);
    };

    // Switching a field to/from "calculated" also flips isHidden — calculated fields are
    // computed server-side and never rendered to the respondent.
    const updateFieldType = (index, type) => {
        const updated = [...form.fields];
        updated[index] = {
            ...updated[index],
            type,
            isHidden: type === "calculated" ? true : (updated[index].isHidden && updated[index].type === "calculated" ? false : updated[index].isHidden),
            ...(type === "calculated" ? { calculation: updated[index].calculation || { expression: "" } } : {}),
        };
        setForm({ ...form, fields: updated });
    };

    // Scoring: { enabled, optionScores: { [option]: points } } — summed into the hidden `score`.
    const updateScoring = (index, patch) => {
        const current = form.fields[index].scoring || { enabled: false, optionScores: {} };
        updateField(index, "scoring", { ...current, ...patch });
    };

    const updateOptionScore = (index, option, raw) => {
        const current = form.fields[index].scoring || { enabled: false, optionScores: {} };
        const optionScores = { ...(current.optionScores || {}) };
        if (raw === "") delete optionScores[option];
        else optionScores[option] = Number(raw);
        updateField(index, "scoring", { ...current, optionScores });
    };

    const updateTheme = (key, value) => {
        setForm({ ...form, theme: { ...form.theme, [key]: value } });
    };

    const applyTemplate = (templateId) => {
        const template = getTemplate(templateId);
        const { name, description, ...themeValues } = template;
        setForm({ ...form, theme: { ...form.theme, ...themeValues } });
        toast(`${template.name || templateId} template applied!`);
    };

    const copyEmbedCode = () => {
        const code = `<iframe src="${formBaseUrl}/f/${formId}" width="100%" height="600" frameborder="0"></iframe>`;
        navigator.clipboard.writeText(code);
        toast("Embed code copied!");
    };

    const copyFormLink = () => {
        navigator.clipboard.writeText(`${formBaseUrl}/f/${formId}`);
        toast(customDomain ? "Form link copied (custom domain)!" : "Form link copied!");
    };

    // Preview style helpers
    const getInputStyle = (theme) => {
        const base = {
            width: "100%",
            padding: "8px 12px",
            fontSize: `${theme?.fontSize || 14}px`,
            backgroundColor: theme?.inputBgColor || "#ffffff",
            color: "#374151",
            outline: "none",
            boxSizing: "border-box",
            borderRadius: `${theme?.borderRadius || 8}px`,
        };
        switch (theme?.inputStyle) {
            case "underline":
                return { ...base, border: "none", borderBottom: `2px solid ${theme?.inputBorderColor || "#d1d5db"}`, borderRadius: 0 };
            case "filled":
                return { ...base, border: `1px solid transparent`, backgroundColor: theme?.inputBgColor === "#ffffff" ? "#f3f4f6" : theme?.inputBgColor };
            default:
                return { ...base, border: `1px solid ${theme?.inputBorderColor || "#d1d5db"}` };
        }
    };

    const getButtonStyle = (theme) => {
        const base = {
            backgroundColor: theme?.primaryColor || "#6366f1",
            color: theme?.buttonTextColor || "#ffffff",
            border: "none",
            padding: "10px 24px",
            fontSize: "15px",
            fontWeight: 600,
            cursor: "not-allowed",
        };
        switch (theme?.buttonStyle) {
            case "pill":
                return { ...base, borderRadius: "9999px" };
            case "square":
                return { ...base, borderRadius: "0px" };
            default:
                return { ...base, borderRadius: `${theme?.borderRadius || 8}px` };
        }
    };

    const getShadowStyle = (shadow) => {
        switch (shadow) {
            case "none": return "none";
            case "md": return "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)";
            case "lg": return "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)";
            default: return "0 1px 3px rgba(0,0,0,0.1)";
        }
    };

    const getPreviewWidth = () => {
        switch (previewDevice) {
            case "mobile": return "375px";
            case "tablet": return "768px";
            default: return "100%";
        }
    };

    const FIELD_TYPE_COLORS = {
        input: "#6366f1",
        textarea: "#8b5cf6",
        email: "#06b6d4",
        number: "#10b981",
        date: "#f59e0b",
        select: "#ec4899",
        radio: "#f97316",
        checkbox: "#ef4444",
    };

    if (fetching) {
        return (
            <div className="min-h-screen max-w-3xl mx-auto px-4 sm:px-6 py-8 font-Rajdhani">
                <div className="mt-10 space-y-4">
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-48" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-72" />
                    <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                </div>
            </div>
        );
    }

    if (!form) {
        return (
            <div className="min-h-screen max-w-3xl mx-auto px-4 sm:px-6 py-8 font-Rajdhani">
                <p className="text-xl mt-10 text-red-500">
                    Form not found.{" "}
                    <button
                        className="underline"
                        onClick={() => navigate("/form-builder")}
                    >
                        Go back
                    </button>
                </p>
            </div>
        );
    }

    return (
        <div className="min-h-screen max-w-7xl mx-auto px-4 sm:px-6 py-6 font-Rajdhani">
            {/* Top bar */}
            <div className="flex flex-wrap items-center justify-between gap-2 mt-4 mb-4 border-b dark:border-gray-700 pb-3">
                <div className="flex items-center gap-3">
                    <button
                        className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                        onClick={() => navigate("/form-builder")}
                    >
                        &larr; Back
                    </button>
                    <input
                        type="text"
                        className="text-xl font-bold bg-transparent border-b border-transparent hover:border-gray-300 focus:border-indigo-500 outline-none px-1 py-0.5 dark:text-white"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                    />
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <button
                        onClick={copyFormLink}
                        className="px-3 py-1 text-sm border-[1px] dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-[#2c2f44]"
                        title="Copy form link"
                    >
                        Copy Link
                    </button>
                    <button
                        onClick={copyEmbedCode}
                        className="px-3 py-1 text-sm border-[1px] dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-[#2c2f44]"
                        title="Copy embed code"
                    >
                        Embed
                    </button>
                    <button
                        onClick={handlePublishToggle}
                        className={`px-3 py-1 text-sm rounded text-white ${
                            form.isPublished
                                ? "bg-green-600"
                                : "bg-gray-400"
                        }`}
                    >
                        {form.isPublished ? "Published" : "Draft"}
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-1 text-sm rounded text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Left panel */}
                <div className="lg:w-1/2 w-full">
                    {/* Tabs */}
                    <div className="flex border-b dark:border-gray-700 mb-4">
                        {["fields", "design", "settings", "integrations"].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-4 py-2 text-sm font-semibold capitalize border-b-2 transition-colors ${
                                    activeTab === tab
                                        ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
                                        : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    {/* Fields Tab */}
                    {activeTab === "fields" && (
                        <div>
                            <div className="mb-4">
                                <label className="text-sm font-semibold">Description</label>
                                <input
                                    type="text"
                                    className="w-full border-[1px] dark:border-gray-600 rounded p-2 mt-1 dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal"
                                    value={form.description || ""}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                />
                            </div>

                            <div className="flex justify-between items-center mb-2">
                                <h3 className="text-lg font-semibold">Fields ({form.fields.length})</h3>
                                <button
                                    onClick={addField}
                                    className="px-3 py-1 text-sm border-[1px] dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-[#2c2f44]"
                                >
                                    + Add Field
                                </button>
                            </div>

                            {form.fields.map((field, idx) => (
                                <div
                                    key={field.fieldId || idx}
                                    className="border rounded-lg p-3 mb-3 bg-gray-50 dark:bg-[#23263a]"
                                    style={{ borderLeft: `3px solid ${FIELD_TYPE_COLORS[field.type] || "#6366f1"}` }}
                                >
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-xs font-medium px-2 py-0.5 rounded" style={{ backgroundColor: FIELD_TYPE_COLORS[field.type] || "#6366f1", color: "white" }}>
                                            {field.type}
                                        </span>
                                        <div className="flex gap-1">
                                            <button onClick={() => moveField(idx, -1)} className="px-2 py-0.5 text-xs border-[1px] dark:border-gray-600 rounded disabled:opacity-30" disabled={idx === 0}>
                                                Up
                                            </button>
                                            <button onClick={() => moveField(idx, 1)} className="px-2 py-0.5 text-xs border-[1px] dark:border-gray-600 rounded disabled:opacity-30" disabled={idx === form.fields.length - 1}>
                                                Down
                                            </button>
                                            <button onClick={() => removeField(idx)} className="px-2 py-0.5 text-xs border-[1px] dark:border-gray-600 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20">
                                                Delete
                                            </button>
                                            <button
                                                onClick={() => setLogicPanelIdx(logicPanelIdx === idx ? null : idx)}
                                                className={`px-2 py-0.5 text-xs border-[1px] rounded transition-colors ${
                                                    logicPanelIdx === idx
                                                        ? "bg-indigo-600 text-white border-indigo-600"
                                                        : (field.logicRules?.length > 0
                                                            ? "border-indigo-400 text-indigo-600 dark:text-indigo-400"
                                                            : "dark:border-gray-600 text-gray-500 hover:border-indigo-400")
                                                }`}
                                                title="Edit logic rules"
                                            >
                                                {field.logicRules?.length > 0 ? `Logic (${field.logicRules.length})` : "Logic"}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="text-xs text-gray-500 dark:text-gray-400">Label</label>
                                            <input
                                                type="text"
                                                className="w-full border-[1px] dark:border-gray-600 rounded p-1 text-sm dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal"
                                                value={field.label || ""}
                                                onChange={(e) => updateField(idx, "label", e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs text-gray-500 dark:text-gray-400">Type</label>
                                            <select
                                                className="w-full border-[1px] dark:border-gray-600 rounded p-1 text-sm dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal"
                                                value={field.type || "input"}
                                                onChange={(e) => updateFieldType(idx, e.target.value)}
                                            >
                                                {FIELD_TYPES.map((t) => (
                                                    <option key={t} value={t}>{t}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs text-gray-500 dark:text-gray-400">Placeholder</label>
                                            <input
                                                type="text"
                                                className="w-full border-[1px] dark:border-gray-600 rounded p-1 text-sm dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal"
                                                value={field.placeholder || ""}
                                                onChange={(e) => updateField(idx, "placeholder", e.target.value)}
                                            />
                                        </div>
                                        <div className="flex items-end gap-2">
                                            <label className="flex items-center gap-1 text-sm font-normal">
                                                <input
                                                    type="checkbox"
                                                    checked={field.isRequired || false}
                                                    onChange={(e) => updateField(idx, "isRequired", e.target.checked)}
                                                />
                                                Required
                                            </label>
                                        </div>
                                    </div>

                                    {["select", "radio", "checkbox"].includes(field.type) && (
                                        <div className="mt-2">
                                            <label className="text-xs text-gray-500 dark:text-gray-400">Options (comma separated)</label>
                                            <input
                                                type="text"
                                                className="w-full border-[1px] dark:border-gray-600 rounded p-1 text-sm dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal"
                                                value={(field.options || []).join(", ")}
                                                onChange={(e) => updateFieldOptions(idx, e.target.value)}
                                                placeholder="Option 1, Option 2, Option 3"
                                            />
                                        </div>
                                    )}

                                    {/* Scoring — per-option point values, summed into the hidden `score` */}
                                    {SCORABLE_TYPES.includes(field.type) && (
                                        <div className="mt-2 border-t dark:border-gray-700 pt-2">
                                            <label className="flex items-center gap-2 text-sm font-normal">
                                                <input
                                                    type="checkbox"
                                                    checked={field.scoring?.enabled || false}
                                                    onChange={(e) => updateScoring(idx, { enabled: e.target.checked })}
                                                />
                                                <span>Enable scoring</span>
                                                <span className="text-sm text-gray-600 dark:text-gray-400">— assign points per option</span>
                                            </label>

                                            {field.scoring?.enabled && (
                                                (field.options || []).filter(Boolean).length > 0 ? (
                                                    <div className="mt-2 space-y-1">
                                                        {(field.options || []).filter(Boolean).map((opt) => (
                                                            <div key={opt} className="flex items-center gap-2">
                                                                <span className="flex-1 text-sm truncate text-gray-600 dark:text-gray-300">{opt}</span>
                                                                <input
                                                                    type="number"
                                                                    className="w-24 border-[1px] dark:border-gray-600 rounded p-1 text-sm dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal"
                                                                    value={field.scoring?.optionScores?.[opt] ?? ""}
                                                                    onChange={(e) => updateOptionScore(idx, opt, e.target.value)}
                                                                    placeholder="points"
                                                                />
                                                            </div>
                                                        ))}
                                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                            Points from every selected option are added to <code>score</code>, usable in logic conditions and calculated fields.
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Add options above to assign points.</p>
                                                )
                                            )}
                                        </div>
                                    )}

                                    {/* Calculated field — expression editor */}
                                    {field.type === "calculated" && (
                                        <div className="mt-2 border-t dark:border-gray-700 pt-2">
                                            <label className="text-xs text-gray-500 dark:text-gray-400">Expression</label>
                                            <input
                                                type="text"
                                                maxLength={500}
                                                className="w-full border-[1px] dark:border-gray-600 rounded p-1 text-sm font-mono dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal"
                                                value={field.calculation?.expression || ""}
                                                onChange={(e) => updateField(idx, "calculation", { expression: e.target.value })}
                                                placeholder="e.g. score * 2 + field_1"
                                            />
                                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                Hidden from respondents; computed on submit. Available variables:{" "}
                                                <code>score</code>
                                                {form.fields.filter((f, i) => i !== idx && f.name).slice(0, 8).map((f) => (
                                                    <span key={f.name}>, <code>{f.name}</code></span>
                                                ))}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Design Tab */}
                    {activeTab === "design" && (
                        <div className="space-y-5">
                            {/* Template Selector */}
                            <div>
                                <h4 className="text-sm font-semibold mb-2">Templates</h4>
                                <div className="grid grid-cols-3 gap-2">
                                    {TEMPLATE_LIST.map((t) => (
                                        <button
                                            key={t.id}
                                            onClick={() => applyTemplate(t.id)}
                                            className={`border-[1px] dark:border-gray-600 rounded-lg p-3 text-left text-xs hover:shadow-md transition-shadow dark:bg-[#23263a] ${
                                                form.theme?.templateId === t.id ? "ring-2 ring-indigo-500" : ""
                                            }`}
                                        >
                                            <div className="flex gap-1 mb-1">
                                                <span className="w-4 h-4 rounded" style={{ backgroundColor: t.primaryColor }} />
                                                <span className="w-4 h-4 rounded border" style={{ backgroundColor: t.backgroundColor }} />
                                            </div>
                                            <span className="font-semibold">{t.name}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Colors */}
                            <div>
                                <h4 className="text-sm font-semibold mb-2">Colors</h4>
                                <div className="grid grid-cols-3 gap-3">
                                    {[
                                        { key: "primaryColor", label: "Primary" },
                                        { key: "backgroundColor", label: "Background" },
                                        { key: "labelColor", label: "Labels" },
                                        { key: "inputBgColor", label: "Input BG" },
                                        { key: "inputBorderColor", label: "Input Border" },
                                        { key: "buttonTextColor", label: "Button Text" },
                                    ].map(({ key, label }) => (
                                        <div key={key}>
                                            <label className="text-xs text-gray-500 dark:text-gray-400">{label}</label>
                                            <div className="flex items-center gap-2 mt-1">
                                                <input
                                                    type="color"
                                                    className="w-8 h-8 rounded cursor-pointer border-0"
                                                    value={form.theme?.[key] || "#6366f1"}
                                                    onChange={(e) => updateTheme(key, e.target.value)}
                                                />
                                                <input
                                                    type="text"
                                                    className="w-20 border-[1px] dark:border-gray-600 rounded px-1 py-0.5 text-xs font-normal dark:bg-[#2c2f44] dark:text-[#99a0ac]"
                                                    value={form.theme?.[key] || ""}
                                                    onChange={(e) => updateTheme(key, e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Typography */}
                            <div>
                                <h4 className="text-sm font-semibold mb-2">Typography</h4>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs text-gray-500 dark:text-gray-400">Font Family</label>
                                        <select
                                            className="w-full border-[1px] dark:border-gray-600 rounded p-1.5 text-sm mt-1 dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal"
                                            value={form.theme?.fontFamily || "Inter"}
                                            onChange={(e) => updateTheme("fontFamily", e.target.value)}
                                        >
                                            {FONT_OPTIONS.map((f) => (
                                                <option key={f} value={f}>{f}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 dark:text-gray-400">Font Size: {form.theme?.fontSize || 14}px</label>
                                        <input
                                            type="range"
                                            min="10"
                                            max="24"
                                            className="w-full mt-1"
                                            value={form.theme?.fontSize || 14}
                                            onChange={(e) => updateTheme("fontSize", parseInt(e.target.value))}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Layout */}
                            <div>
                                <h4 className="text-sm font-semibold mb-2">Layout</h4>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs text-gray-500 dark:text-gray-400">Container Width: {form.theme?.containerWidth || 600}px</label>
                                        <input type="range" min="320" max="1200" className="w-full mt-1" value={form.theme?.containerWidth || 600} onChange={(e) => updateTheme("containerWidth", parseInt(e.target.value))} />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 dark:text-gray-400">Padding: {form.theme?.padding || 32}px</label>
                                        <input type="range" min="8" max="80" className="w-full mt-1" value={form.theme?.padding || 32} onChange={(e) => updateTheme("padding", parseInt(e.target.value))} />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 dark:text-gray-400">Field Spacing: {form.theme?.fieldSpacing || 16}px</label>
                                        <input type="range" min="0" max="64" className="w-full mt-1" value={form.theme?.fieldSpacing || 16} onChange={(e) => updateTheme("fieldSpacing", parseInt(e.target.value))} />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 dark:text-gray-400">Border Radius: {form.theme?.borderRadius || 8}px</label>
                                        <input type="range" min="0" max="50" className="w-full mt-1" value={form.theme?.borderRadius || 8} onChange={(e) => updateTheme("borderRadius", parseInt(e.target.value))} />
                                    </div>
                                </div>
                            </div>

                            {/* Styles */}
                            <div>
                                <h4 className="text-sm font-semibold mb-2">Styles</h4>
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Input Style</label>
                                        <div className="flex gap-2">
                                            {["bordered", "underline", "filled"].map((s) => (
                                                <button
                                                    key={s}
                                                    onClick={() => updateTheme("inputStyle", s)}
                                                    className={`px-3 py-1 text-xs border-[1px] dark:border-gray-600 rounded capitalize ${form.theme?.inputStyle === s ? "bg-indigo-100 border-indigo-500 text-indigo-700 dark:bg-indigo-900 dark:border-indigo-500 dark:text-indigo-300" : ""}`}
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Button Style</label>
                                        <div className="flex gap-2">
                                            {["rounded", "pill", "square"].map((s) => (
                                                <button
                                                    key={s}
                                                    onClick={() => updateTheme("buttonStyle", s)}
                                                    className={`px-3 py-1 text-xs border-[1px] dark:border-gray-600 rounded capitalize ${form.theme?.buttonStyle === s ? "bg-indigo-100 border-indigo-500 text-indigo-700 dark:bg-indigo-900 dark:border-indigo-500 dark:text-indigo-300" : ""}`}
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Shadow</label>
                                        <div className="flex gap-2">
                                            {["none", "sm", "md", "lg"].map((s) => (
                                                <button
                                                    key={s}
                                                    onClick={() => updateTheme("shadow", s)}
                                                    className={`px-3 py-1 text-xs border-[1px] dark:border-gray-600 rounded ${form.theme?.shadow === s ? "bg-indigo-100 border-indigo-500 text-indigo-700 dark:bg-indigo-900 dark:border-indigo-500 dark:text-indigo-300" : ""}`}
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Settings Tab */}
                    {activeTab === "settings" && (
                        <div className="space-y-4">
                            <div>
                                <label className="text-sm font-semibold">Display Mode</label>
                                <div className="flex gap-2 mt-2">
                                    {[
                                        { value: "classic", label: "Classic", desc: "All questions on one page" },
                                        { value: "conversational", label: "Conversational", desc: "One question at a time, Typeform-style" },
                                    ].map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setForm({ ...form, display_mode: opt.value })}
                                            className={`flex-1 border-[1px] dark:border-gray-600 rounded-lg p-2 text-xs text-left ${
                                                (form.display_mode || "classic") === opt.value
                                                    ? "bg-indigo-100 border-indigo-500 text-indigo-700 dark:bg-indigo-900 dark:border-indigo-500 dark:text-indigo-300"
                                                    : "hover:bg-gray-50 dark:hover:bg-[#2c2f44]"
                                            }`}
                                        >
                                            <span className="font-semibold block">{opt.label}</span>
                                            <span className="text-gray-500 dark:text-gray-400 font-normal">{opt.desc}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-semibold">Logo URL</label>
                                <input
                                    type="text"
                                    className="w-full border-[1px] dark:border-gray-600 rounded p-2 mt-1 dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal"
                                    value={form.logo || ""}
                                    placeholder="https://example.com/logo.png"
                                    onChange={(e) => setForm({ ...form, logo: e.target.value })}
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={form.captchaEnabled || false}
                                    onChange={(e) => setForm({ ...form, captchaEnabled: e.target.checked })}
                                />
                                <label className="text-sm font-normal">Enable reCAPTCHA</label>
                            </div>
                            <div>
                                <label className="text-sm font-semibold">Submit Button Text</label>
                                <input
                                    type="text"
                                    className="w-full border-[1px] dark:border-gray-600 rounded p-2 mt-1 dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal"
                                    value={form.submitButtonText || "Submit"}
                                    onChange={(e) => setForm({ ...form, submitButtonText: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-semibold">Success Message</label>
                                <input
                                    type="text"
                                    className="w-full border-[1px] dark:border-gray-600 rounded p-2 mt-1 dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal"
                                    value={form.successMessage || "Thank you for your response!"}
                                    onChange={(e) => setForm({ ...form, successMessage: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-semibold">Success Message Placement</label>
                                <div className="flex gap-2 mt-2">
                                    {[
                                        { value: "fullpage", label: "Full Page", desc: "Replaces the form with the success message" },
                                        { value: "inline", label: "Inline", desc: "Shows above the form, form stays visible" },
                                        { value: "modal", label: "Modal", desc: "Shows as an overlay popup" },
                                    ].map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setForm({ ...form, successPlacement: opt.value })}
                                            className={`flex-1 border-[1px] dark:border-gray-600 rounded-lg p-2 text-xs text-left ${
                                                (form.successPlacement || "fullpage") === opt.value
                                                    ? "bg-indigo-100 border-indigo-500 text-indigo-700 dark:bg-indigo-900 dark:border-indigo-500 dark:text-indigo-300"
                                                    : "hover:bg-gray-50 dark:hover:bg-[#2c2f44]"
                                            }`}
                                        >
                                            <span className="font-semibold block">{opt.label}</span>
                                            <span className="text-gray-500 dark:text-gray-400 font-normal">{opt.desc}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={form.notifyEmail !== false}
                                    onChange={(e) => setForm({ ...form, notifyEmail: e.target.checked })}
                                />
                                <label className="text-sm font-normal">Email notifications for new responses</label>
                            </div>
                        </div>
                    )}

                    {/* Integrations Tab */}
                    {activeTab === "integrations" && (
                        <FormIntegrationsTab formId={formId} form={form} />
                    )}
                </div>

                {/* Right panel - Live preview */}
                <div className="lg:w-1/2 w-full">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="text-lg font-semibold">Preview</h3>
                        <div className="flex gap-1 border-[1px] dark:border-gray-600 rounded p-0.5">
                            {["desktop", "tablet", "mobile"].map((d) => (
                                <button
                                    key={d}
                                    onClick={() => setPreviewDevice(d)}
                                    className={`px-2 py-0.5 text-xs rounded capitalize ${
                                        previewDevice === d ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300" : "text-gray-500"
                                    }`}
                                >
                                    {d}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div
                        className="border-[1px] dark:border-gray-700 rounded-lg overflow-auto mx-auto transition-all"
                        style={{
                            maxWidth: getPreviewWidth(),
                            backgroundColor: form.theme?.backgroundColor || "#ffffff",
                            fontFamily: form.theme?.fontFamily || "Inter",
                            padding: `${form.theme?.padding || 32}px`,
                            boxShadow: getShadowStyle(form.theme?.shadow),
                        }}
                    >
                        {form.logo && (
                            <img
                                src={form.logo}
                                alt="Form logo"
                                className="h-12 mb-4"
                            />
                        )}
                        <h2
                            className="text-2xl font-bold mb-1"
                            style={{ color: form.theme?.primaryColor || "#6366f1" }}
                        >
                            {form.title}
                        </h2>
                        {form.description && (
                            <p style={{ color: form.theme?.labelColor || "#374151", opacity: 0.7, marginBottom: "16px", fontWeight: "normal" }}>
                                {form.description}
                            </p>
                        )}

                        {form.fields.map((field, idx) => (
                            <div key={field.fieldId || idx} style={{ marginBottom: `${form.theme?.fieldSpacing || 16}px` }}>
                                <label style={{ display: "block", fontSize: "14px", fontWeight: 600, color: form.theme?.labelColor || "#374151", marginBottom: "4px" }}>
                                    {field.label}
                                    {field.isRequired && <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>}
                                </label>
                                {field.type === "textarea" ? (
                                    <textarea style={getInputStyle(form.theme)} placeholder={field.placeholder} disabled rows={3} />
                                ) : field.type === "select" ? (
                                    <select style={getInputStyle(form.theme)} disabled>
                                        <option>{field.placeholder || "Select..."}</option>
                                        {(field.options || []).map((opt, i) => <option key={i}>{opt}</option>)}
                                    </select>
                                ) : field.type === "radio" ? (
                                    <div>
                                        {(field.options || []).map((opt, i) => (
                                            <label key={i} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: `${form.theme?.fontSize || 14}px`, marginBottom: "4px", fontWeight: "normal", color: form.theme?.labelColor || "#374151" }}>
                                                <input type="radio" name={`preview_${field.name}`} disabled />
                                                {opt}
                                            </label>
                                        ))}
                                    </div>
                                ) : field.type === "checkbox" ? (
                                    <div>
                                        {(field.options || []).length > 0 ? (
                                            field.options.map((opt, i) => (
                                                <label key={i} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: `${form.theme?.fontSize || 14}px`, marginBottom: "4px", fontWeight: "normal", color: form.theme?.labelColor || "#374151" }}>
                                                    <input type="checkbox" disabled />
                                                    {opt}
                                                </label>
                                            ))
                                        ) : (
                                            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: `${form.theme?.fontSize || 14}px`, fontWeight: "normal", color: form.theme?.labelColor || "#374151" }}>
                                                <input type="checkbox" disabled />
                                                {field.label}
                                            </label>
                                        )}
                                    </div>
                                ) : (
                                    <input
                                        type={field.type || "text"}
                                        style={getInputStyle(form.theme)}
                                        placeholder={field.placeholder}
                                        disabled
                                    />
                                )}
                            </div>
                        ))}

                        <button style={getButtonStyle(form.theme)} disabled>
                            {form.submitButtonText || "Submit"}
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <p className="mt-3 text-red-600 dark:text-red-400 font-medium">
                    {error}
                </p>
            )}

            <ToastContainer
                position="top-right"
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme={localStorage.getItem("theme") === "dark" ? "dark" : "light"}
            />

            {/* Logic builder side panel */}
            {logicPanelIdx !== null && form.fields[logicPanelIdx] && (
                <>
                    {/* Backdrop (click to close) */}
                    <div
                        className="fixed inset-0 z-30 bg-black/20"
                        onClick={() => setLogicPanelIdx(null)}
                    />
                    <LogicBuilder
                        field={form.fields[logicPanelIdx]}
                        fieldIdx={logicPanelIdx}
                        allFields={form.fields}
                        onUpdate={(updatedField) => {
                            const updated = [...form.fields];
                            updated[logicPanelIdx] = updatedField;
                            setForm({ ...form, fields: updated });
                        }}
                        onClose={() => setLogicPanelIdx(null)}
                        onShowPreview={() => setShowFlowPreview(true)}
                    />
                </>
            )}

            {/* Flow preview modal */}
            {showFlowPreview && (
                <FlowPreview
                    fields={form.fields}
                    onClose={() => setShowFlowPreview(false)}
                    onJumpToField={(idx) => {
                        setLogicPanelIdx(idx);
                        setActiveTab("fields");
                    }}
                />
            )}
        </div>
    );
}

// ── Per-form integrations tab ─────────────────────────────────────────────────

function FormIntegrationsTab({ formId, form }) {
    const {
        providers,
        fetchProviders,
        listFormIntegrations,
        createFormIntegration,
        deleteFormIntegration,
        testAction,
    } = useIntegrations();

    const [formIntegrations, setFormIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [selectedConn, setSelectedConn] = useState("");
    const [selectedAction, setSelectedAction] = useState("");
    const [actionConfig, setActionConfig] = useState({});
    const [saveError, setSaveError] = useState(null);
    const [testResult, setTestResult] = useState(null);  // { success, output, latencyMs } | { error }
    const [testing, setTesting] = useState(false);

    const visibleFields = (form?.fields || [])
        .filter(f => f.type !== "calculated" && !f.isHidden);
    const formFields = visibleFields.map(f => f.label || f.name || f._id);
    // Richer defs for integrations that map by field name but display the label
    // (e.g. Google Sheets column picker). name is the payload key, label is shown.
    const fieldDefs = visibleFields.map(f => ({ name: f.name || f._id, label: f.label || f.name || f._id }));

    const load = useCallback(async () => {
        setLoading(true);
        try {
            await fetchProviders();
            const items = await listFormIntegrations(formId);
            setFormIntegrations(items);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [formId, fetchProviders, listFormIntegrations]);

    useEffect(() => { load(); }, [load]);

    // Flatten all active connections across providers, including full action objects
    const allConnections = providers.flatMap(p =>
        (p.connections || []).filter(c => c.status === "active").map(c => ({
            ...c,
            providerName: p.name,
            providerSlug: p.slug,
            actionObjects: p.supportedActions || [],
        }))
    );

    const selectedConnection = allConnections.find(c => c._id === selectedConn);
    const selectedActionObj = (selectedConnection?.actionObjects || []).find(a => a.action === selectedAction);
    const configSchema = selectedActionObj?.configSchema || [];

    const handleConnChange = (connId) => {
        setSelectedConn(connId);
        setSelectedAction("");
        setActionConfig({});
        setTestResult(null);
    };

    const handleActionChange = (action) => {
        setSelectedAction(action);
        setActionConfig({});
        setTestResult(null);
    };

    // Required action parameters, checked before the integration can be attached.
    // Without this a Sheets integration could be saved with no spreadsheet and
    // would only fail later, on a real submission, where nobody sees it.
    const isBlankValue = (v) =>
        v === undefined || v === null || v === "" ||
        (Array.isArray(v) && v.length === 0) ||
        (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);
    const missingParams = configSchema
        .filter(f => f.required && isBlankValue(actionConfig[f.key]))
        .map(f => f.label || f.key);

    const handleAdd = async () => {
        if (!selectedConn || !selectedAction) return;
        if (missingParams.length) {
            setSaveError(`Still needed: ${missingParams.join(", ")}`);
            return;
        }
        setSaveError(null);
        setAdding(true);
        try {
            await createFormIntegration(formId, selectedConn, selectedAction, actionConfig);
            setSelectedConn("");
            setSelectedAction("");
            setActionConfig({});
            setTestResult(null);
            await load();
        } catch (e) {
            setSaveError(e.message);
        } finally {
            setAdding(false);
        }
    };

    const handleTest = async () => {
        if (!selectedConn || !selectedAction) return;
        setTesting(true);
        setTestResult(null);
        try {
            let testPayload = actionConfig;
            // For the Sheets append, inject sample values keyed by field name so the
            // test writes a real-looking row (config alone carries no answers).
            if (selectedAction === "sheets.append_row") {
                const sample = {};
                fieldDefs.forEach((f, i) => { sample[f.name] = `Sample ${f.label || `value ${i + 1}`}`; });
                testPayload = {
                    ...sample,
                    ...actionConfig,
                    _meta: { formTitle: form?.title || "Test Form", submittedAt: new Date().toISOString(), responseId: "test" },
                };
            }
            const result = await testAction(selectedConn, selectedAction, testPayload);
            setTestResult({ success: true, output: result.output, latencyMs: result.latencyMs });
        } catch (e) {
            setTestResult({ success: false, error: e.message });
        } finally {
            setTesting(false);
        }
    };

    const handleDelete = async (integrationId) => {
        if (!window.confirm("Remove this integration from the form?")) return;
        try {
            await deleteFormIntegration(formId, integrationId);
            await load();
        } catch (e) {
            alert(e.message);
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Form integrations</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Actions run automatically when this form receives a submission.{" "}
                    <Link to="/settings/integrations" className="text-indigo-600 dark:text-indigo-400 underline">
                        Manage connections →
                    </Link>
                </p>
            </div>

            {loading && <p className="text-sm text-gray-400 dark:text-gray-500">Loading…</p>}

            {!loading && formIntegrations.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center border-[1px] border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                    No integrations yet. Add one below.
                </p>
            )}

            {formIntegrations.map(fi => (
                <div key={fi._id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/40 rounded-lg text-sm">
                    <div>
                        <span className="font-medium text-gray-900 dark:text-white">{fi.providerSlug}</span>
                        <span className="mx-1 text-gray-400 dark:text-gray-500">·</span>
                        <span className="text-gray-500 dark:text-gray-400 font-mono text-xs">{fi.action}</span>
                    </div>
                    <button
                        onClick={() => handleDelete(fi._id)}
                        className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition"
                    >
                        Remove
                    </button>
                </div>
            ))}

            {allConnections.length === 0 ? (
                <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg text-sm text-indigo-700 dark:text-indigo-400">
                    No connected integrations yet.{" "}
                    <Link to="/settings/integrations" className="font-medium underline">Connect a service →</Link>
                </div>
            ) : (
                <div className="pt-2 border-t dark:border-gray-700 space-y-3">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Add integration</p>

                    {/* Connection + action selectors */}
                    <div className="flex gap-2">
                        <select
                            value={selectedConn}
                            onChange={e => handleConnChange(e.target.value)}
                            className="flex-1 text-sm border-[1px] border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="">Select connection…</option>
                            {allConnections.map(c => (
                                <option key={c._id} value={c._id}>
                                    {c.providerName}{c.credentialMetadata?.teamName ? ` (${c.credentialMetadata.teamName})` : ""}
                                </option>
                            ))}
                        </select>
                        <select
                            value={selectedAction}
                            onChange={e => handleActionChange(e.target.value)}
                            disabled={!selectedConn}
                            className="flex-1 text-sm border-[1px] border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                        >
                            <option value="">Select action…</option>
                            {(selectedConnection?.actionObjects || []).map(a => (
                                <option key={a.action} value={a.action}>{a.action}</option>
                            ))}
                        </select>
                    </div>

                    {/* Per-action config editor */}
                    {selectedAction && (
                        <div className="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg space-y-3">
                            {selectedActionObj?.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">{selectedActionObj.description}</p>
                            )}
                            <IntegrationConfigEditor
                                schema={configSchema}
                                value={actionConfig}
                                onChange={setActionConfig}
                                formFields={formFields}
                                fieldDefs={fieldDefs}
                                providerSlug={selectedConnection?.providerSlug}
                                formTitle={form?.title}
                                onCreateSheet={async (title, headers) => {
                                    const r = await testAction(selectedConn, "sheets.create_spreadsheet", { title, headers });
                                    if (!r?.success) throw new Error(r?.error || "Failed to create sheet");
                                    return r.output;
                                }}
                            />

                            {/* Test connection button */}
                            <div className="pt-2 border-t dark:border-gray-600">
                                <button
                                    onClick={handleTest}
                                    disabled={testing}
                                    className="text-xs px-3 py-1.5 border-[1px] border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition"
                                >
                                    {testing ? "Sending test…" : "Send test data"}
                                </button>

                                {testResult && (
                                    <div className={`mt-2 p-2 rounded-lg text-xs ${testResult.success ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400" : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400"}`}>
                                        {testResult.success ? (
                                            <>
                                                <span className="font-medium">Success</span>
                                                {testResult.latencyMs && <span className="ml-1 text-gray-500 dark:text-gray-400">({testResult.latencyMs}ms)</span>}
                                                {testResult.output && (
                                                    <pre className="mt-1 text-xs overflow-x-auto whitespace-pre-wrap break-all opacity-80">
                                                        {JSON.stringify(testResult.output, null, 2)}
                                                    </pre>
                                                )}
                                            </>
                                        ) : (
                                            <><span className="font-medium">Failed:</span> {testResult.error}</>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {saveError && <p className="text-xs text-red-600 dark:text-red-400">{saveError}</p>}
                    <button
                        onClick={handleAdd}
                        disabled={!selectedConn || !selectedAction || adding}
                        className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition"
                    >
                        {adding ? "Adding…" : "Add integration"}
                    </button>
                </div>
            )}
        </div>
    );
}

export default FormEdit;
